// src/components/session/DriverSessionList.js
import React, { useMemo } from 'react';
import { 
  Card, 
  CardContent, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  TableContainer,
  Paper,
  Typography,
  Chip,
  Box,
  IconButton,
  Stack,
  Tooltip
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BoltIcon from '@mui/icons-material/Bolt';
import PaymentIcon from '@mui/icons-material/Payment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LoadingSpinner from '../common/Loadingspinner';
import ErrorAlert from '../common/ErrorAlert';
import { formatInTimeZone } from 'date-fns-tz';
import { getChargers } from '../../api/chargerAPI';

// Utility functions for formatting (matching SessionList)
const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString("en-GB");
  } catch {
    return dateString;
  }
};

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'started':
      return 'primary';
    case 'failed':
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};

// Helper function to determine if payment button should be shown (matching SessionList)
const shouldShowPaymentButton = (session) => {
  // Show payment button if:
  // 1. Session is completed (has an end time)
  // 2. Session has a cost greater than 0
  // 3. Session payment status is not already 'paid', 'completed', 'succeeded', or 'success'
  return session.ChargerSessionEnd && 
         session.ChargerSessionCost && 
         session.ChargerSessionCost > 0 &&
         (!session.ChargerSessionPaymentStatus || 
          !['paid', 'completed', 'succeeded', 'success'].includes(session.ChargerSessionPaymentStatus?.toLowerCase()));
};

const DriverSessionList = ({ 
  sessions, 
  isLoading, 
  isError, 
  error
}) => {
  // Fetch all chargers for lookup (matching SessionList)
  const { data: allChargers } = useQuery({
    queryKey: ['all-chargers'],
    queryFn: () => getChargers({})
  });

  // Create lookup map for charger ID -> name (matching SessionList)
  const chargerLookup = useMemo(() => {
    if (!allChargers) return {};
    const lookup = {};
    allChargers.forEach(charger => {
      lookup[charger.ChargerId] = charger.ChargerName;
    });
    return lookup;
  }, [allChargers]);
  
  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading sessions'} />;
  }

  if (!sessions || sessions.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <BoltIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No charging sessions found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your charging sessions will appear here once you start charging
        </Typography>
      </Box>
    );
  }

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent sx={{ padding: 0 }}>
        <TableContainer component={Paper} sx={{ maxHeight: 650, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="120px">Session ID</TableCell>
                <TableCell width="120px">Charger Name</TableCell>
                <TableCell width="100px">Connector</TableCell>
                <TableCell width="180px">Start Time</TableCell>
                <TableCell width="180px">End Time</TableCell>
                <TableCell width="120px">Duration</TableCell>
                <TableCell width="120px">Energy</TableCell>
                <TableCell width="100px">Cost</TableCell>
                <TableCell width="100px">Status</TableCell>
                <TableCell width="140px" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => {
                // Get charger name using lookup (matching SessionList)
                const chargerName = chargerLookup[session.ChargerSessionChargerId] || `${session.ChargerSessionChargerId}`;
                
                return (
                  <TableRow key={session.ChargeSessionId}>
                    <TableCell>#{session.ChargeSessionId}</TableCell>
                    
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {chargerName}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Chip 
                        label={`#${session.ChargerSessionConnectorId}`} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                        {formatDateTime(session.ChargerSessionStart)}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      {session.ChargerSessionEnd ? formatDateTime(session.ChargerSessionEnd) : '-'}
                    </TableCell>
                    
                    <TableCell>
                      {formatDuration(session.ChargerSessionDuration)}
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <BoltIcon fontSize="small" sx={{ mr: 0.5, color: 'warning.main' }} />
                        {session.ChargerSessionEnergyKWH ? 
                          `${session.ChargerSessionEnergyKWH.toFixed(2)} kWh` : 
                          '-'
                        }
                      </Box>
                    </TableCell>

                    <TableCell>
                      {session.ChargerSessionCost ? 
                        `$${session.ChargerSessionCost.toFixed(2)}` : 
                        '-'
                      }
                    </TableCell>
                    
                    <TableCell>
                      <Chip 
                        label={session.ChargerSessionStatus || 'Unknown'} 
                        color={getStatusColor(session.ChargerSessionStatus)}
                        size="small"
                      />
                    </TableCell>
                    
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton 
                            component={Link} 
                            to={`/sessions/${session.ChargeSessionId}`}
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {/* {shouldShowPaymentButton(session) && (
                          <Tooltip title="Make Payment">
                            <IconButton
                              component={Link}
                              to={`/sessions/${session.ChargeSessionId}/payment`}
                              size="small"
                              sx={{ 
                                backgroundColor: 'primary.main', 
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'primary.dark'
                                }
                              }}
                            >
                              <PaymentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )} */}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default DriverSessionList;